<template>
  <div class="group focus-within:!text-gray-100" :style="divWidth">
    <t-label v-if="label" :opened="opened">{{ label }}</t-label>
    <div ref="outsideAutoComplete" class="relative">
      <div class="cursor-pointer" aria-haspopup="true" :aria-expanded="opened">
        <TInputWithIcon
          ref="autocomplete-input"
          v-model="localSearch"
          :has-icon="hasIcon"
          :disabled="disabled"
          :placeholder="placeholder"
          :opened="opened"
          :error-input="errorInput"
          :error-text="errorText"
          @on-focus="onFocus"
          @on-blur="onBlur"
          @keydown="$emit('on-keydown', $event)"
        >
          <template v-if="hasIcon && suggestions.length > 0" #icon>
            <button
              role="button"
              class="flex cursor-pointer items-center justify-center rounded bg-transparent text-white pr-0.5 pl-1.5 h-5"
              data-testid="suggestion-icon"
              @click="onOpenClick"
            >
              <font-awesome-icon :icon="['fas', 'caret-down']" />
            </button>
          </template>
        </TInputWithIcon>
      </div>

      <transition name="slide-fade-dropdown">
        <ul
          v-if="filteredSuggestions.length > 0 && opened"
          ref="dropdown-list"
          class="absolute left-[-1px] top-0 z-10 mt-[36px] max-h-[240px] w-full list-none overflow-hidden rounded-md !border-t-0 bg-gray-600 p-0 font-inter text-xs font-medium leading-6 text-gray-100"
        >
          <VueCustomScrollbar
            :style="{
              height: calculatedScrollHeight + 'px',
              position: 'relative',
            }"
            :settings="{ wheelPropagation: false }"
          >
            <li
              v-for="(suggestion, i) in filteredSuggestions"
              :key="i"
              class="cursor-pointer px-3 py-1 hover:bg-gray-400 hover:text-gray-50"
              @click="selectionChanged(suggestion)"
            >
              <p
                style="
                  margin: 0;
                  padding: 0;
                  display: flex;
                  align-items: center;
                "
                v-html="truncateString(suggestion)"
              ></p>
            </li>
          </VueCustomScrollbar>
        </ul>
      </transition>
    </div>
  </div>
</template>
<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import TInputWithIcon from "../TInputWithIcon/TInputWithIcon.vue";
import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
import { onClickOutside } from "@vueuse/core";
import { ref, watch, computed, useTemplateRef } from "vue";

type Props = {
  modelValue: string;
  label?: string;
  truncateLength?: number;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
  suggestions?: string[];
  hasIcon?: boolean;
  errorInput?: boolean;
  errorText?: string;
};

const props = withDefaults(defineProps<Props>(), {
  truncateLength: 45,
  width: "100%",
  suggestions: () => [],
});

const emit = defineEmits(["update:modelValue", "on-blur", "on-keydown"]);

const opened = ref(false);
const outsideAutoComplete = useTemplateRef("outsideAutoComplete");
const stopWatch = ref(false);
const isFocused = ref(false);

const localSearch = computed({
  get() {
    return props.modelValue;
  },
  set(val: string) {
    emit("update:modelValue", val);
  },
});

const divWidth = computed(() => {
  return `width: ${props.width};`;
});

const calculatedScrollHeight = computed(() => {
  if (filteredSuggestions.value.length < 6) {
    return filteredSuggestions.value.length * 32;
  }

  return 240;
});

const filteredSuggestions = computed(() => {
  if (!isFocused.value && props.suggestions.includes(localSearch.value)) {
    return props.suggestions;
  }
  return props.suggestions.filter((item) => {
    return item
      .toLowerCase()
      .trim()
      .includes(localSearch.value.toLowerCase().trim());
  });
});

watch(localSearch, (n, o) => {
  if (stopWatch.value) {
    stopWatch.value = false;
    return;
  }

  if (n !== o && isFocused.value) {
    open();
    return;
  }
});

function onOpenClick() {
  open();
}

function selectionChanged(option: string) {
  stopWatch.value = true;

  emit("update:modelValue", option);
  opened.value = false;
}

function open() {
  opened.value = true;
}

function truncateString(str: string, n = props.truncateLength) {
  return str.length > n ? str.substring(0, n - 1) + "&hellip;" : str;
}

function close() {
  opened.value = false;
}

onClickOutside(outsideAutoComplete, () => {
  close();
});

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
  emit("on-blur");
}
</script>
<style lang="scss"></style>
